import React, { useState, useMemo } from 'react';
import { useHistory, useParams, Redirect } from 'react-router-dom';
import { Button, Spin, Empty, Input, Dropdown, Drawer } from 'antd-v5';
import { useQuery } from 'react-query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { connect } from 'react-redux';
import dayjs from 'dayjs';
import StandardLayoutV2 from '../../../../Layout/StandardLayoutV2';
import EventKPICards from '../components/EventKPICards';
import AudienceTable from '../components/AudienceTable';
import EventLinksModal from '../components/EventLinksModal';
import AddEditAppointment from '../../../Clients/QuickView/Appointments/AddEditAppointment';
import ConversationWindow from '../../../../../components/Layout/chat/chat';
import { useEvent, useEventAudience, useEventSalesAttribution } from '../../../../../hooks/query/useEvents';
import {
  getClientChat,
  resetAutoReminderId,
  setMsgReminderId,
  setMsgReminderDescription,
  setMsgClient,
} from '../../../../../reducers/messaging';
import getClientPaymentStatus from '../../../../../components/Layout/GetClientPaymentStatus';
import { getRequest } from '../../../../../core/apiRequests';
import { getAppointment } from '../../../../../services/appointments';
import './styles.scss';

const EventDetails = ({ auth }) => {
  const history = useHistory();
  const { eventId } = useParams();
  const { events: eventsFlag } = useFlags();

  // Redirect if feature flag is disabled
  if (!eventsFlag) {
    return <Redirect to="/" />;
  }

  const [showLinksModal, setShowLinksModal] = useState(false);
  const [showChatDrawer, setShowChatDrawer] = useState(false);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [appointmentClient, setAppointmentClient] = useState(null);
  const [editAppointment, setEditAppointment] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Multi-filter state: null = show all, true/false = filter active
  const [filters, setFilters] = useState({
    excluded: false, // false = included only (default), true = excluded only, null = all
    hasAppointment: null, // null = all, true = with appointment, false = without
  });

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ field: null, order: null });

  const pageSize = 20;

  // Fetch event details
  const {
    data: eventData,
    isLoading: isLoadingEvent,
    error,
  } = useEvent({
    eventId,
  });

  // Build audience query with server-side excluded filter (memoized for stable React Query key)
  const audienceQuery = useMemo(() => {
    if (filters.excluded === null) return {};
    return { excluded: filters.excluded };
  }, [filters.excluded]);

  // Fetch audience data with server-side exclusion filter
  const {
    data: audienceData,
    isLoading: isLoadingAudience,
    refetch: refetchAudience,
  } = useEventAudience({
    eventId,
    query: audienceQuery,
  });

  // Fetch sales attribution data asynchronously
  const { data: salesAttributionData, isLoading: isLoadingSales } = useEventSalesAttribution({
    eventId,
  });

  // Fetch associates for appointment modal
  const { data: associates = [] } = useQuery(
    ['associates-list'],
    async () => {
      const reqObj = {
        params: ['associates'],
        loadingStatusOff: true,
      };
      const response = await getRequest(reqObj);
      return response;
    },
    {
      enabled: !!auth.merchantId,
      refetchOnWindowFocus: false,
      initialData: [],
    },
  );

  const event = eventData || {};
  const allAudience = audienceData?.data || [];
  const totalAudience = audienceData?.total || allAudience.length;
  // Use audienceCount (excludes excluded clients) for KPI display
  const audienceCount = event.audience?.audienceCount ?? audienceData?.audienceCount ?? totalAudience;
  const recentContactPercentage = audienceData?.recentContactPercentage;
  const recentContactCount = audienceData?.recentContactCount;

  // Helper to get client name
  const getClientName = client => client.name || `${client.firstName || ''} ${client.lastName || ''}`.trim();

  // Helper to get associate name
  const getAssociateName = client => {
    if (!client.associate) return '';
    return typeof client.associate === 'string' ? client.associate : client.associate?.name || '';
  };

  // Helper to get appointment date
  const getAppointmentDate = client => client.appointmentDate || client.appointments?.[0]?.appointmentDate;

  // Client-side filtering, sorting, and pagination
  const { displayedAudience, filteredTotal } = useMemo(() => {
    let filtered = [...allAudience];

    // Search filter (name, associate)
    if (searchText) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(client => {
        const name = getClientName(client).toLowerCase();
        const associate = getAssociateName(client).toLowerCase();
        return name.includes(search) || associate.includes(search);
      });
    }

    // Note: excluded/included filter is applied server-side via query param

    // Appointment filter
    if (filters.hasAppointment !== null) {
      filtered = filtered.filter(c => {
        const hasAppt = !!getAppointmentDate(c);
        return hasAppt === filters.hasAppointment;
      });
    }

    // Sorting
    if (sortConfig.field && sortConfig.order) {
      filtered.sort((a, b) => {
        let aVal;
        let bVal;
        const { field } = sortConfig;

        if (field === 'name') {
          aVal = getClientName(a).toLowerCase();
          bVal = getClientName(b).toLowerCase();
        } else if (field === 'associate') {
          aVal = getAssociateName(a).toLowerCase();
          bVal = getAssociateName(b).toLowerCase();
        } else if (field === 'lastContactDate') {
          aVal = a.lastContactDate ? dayjs(a.lastContactDate).valueOf() : 0;
          bVal = b.lastContactDate ? dayjs(b.lastContactDate).valueOf() : 0;
        } else if (field === 'appointmentDate') {
          aVal = getAppointmentDate(a) ? dayjs(getAppointmentDate(a)).valueOf() : 0;
          bVal = getAppointmentDate(b) ? dayjs(getAppointmentDate(b)).valueOf() : 0;
        } else if (field === 'attributedSales') {
          aVal = a.attributedSales || 0;
          bVal = b.attributedSales || 0;
        } else {
          return 0;
        }

        if (aVal < bVal) return sortConfig.order === 'ascend' ? -1 : 1;
        if (aVal > bVal) return sortConfig.order === 'ascend' ? 1 : -1;
        return 0;
      });
    }

    // Pagination
    const start = (currentPage - 1) * pageSize;
    return {
      displayedAudience: filtered.slice(start, start + pageSize),
      filteredTotal: filtered.length,
    };
  }, [allAudience, searchText, filters, sortConfig, currentPage]);

  // Format date range
  const formatDateRange = () => {
    const { startDate, endDate } = event;

    if (!startDate) return 'No dates set';

    const start = dayjs(startDate);
    const end = endDate ? dayjs(endDate) : null;

    if (!end || start.isSame(end, 'day')) {
      return start.format('ddd MMM D, YYYY');
    }

    return `${start.format('ddd MMM D, YYYY')} - ${end.format('ddd MMM D, YYYY')}`;
  };

  const handleEditDetails = () => {
    history.push(`/Events/edit-event/${eventId}`);
  };

  const handleSearch = value => {
    setSearchText(value);
    setCurrentPage(1);
  };

  // Sort handler - manually toggle between ascend/descend
  const handleSort = field => {
    setSortConfig(prev => {
      // If clicking same column, toggle order
      if (prev.field === field) {
        return { field, order: prev.order === 'ascend' ? 'descend' : 'ascend' };
      }
      // New column, start with ascend
      return { field, order: 'ascend' };
    });
    setCurrentPage(1);
  };

  // Generic filter handler for any filter type
  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({ ...prev, [filterKey]: value }));
    setCurrentPage(1);
  };

  // Clear all filters (reset to defaults)
  const clearAllFilters = () => {
    setFilters({ excluded: false, hasAppointment: null });
    setCurrentPage(1);
  };

  // Check if any filters are active (non-default values)
  const hasActiveFilters = filters.excluded !== false || filters.hasAppointment !== null;

  // Chat drawer handlers
  const handleOpenChat = async clientRecord => {
    // Prepare client object for messaging system
    const clientId = clientRecord.clientId || clientRecord.id;
    const clientName = clientRecord.name || `${clientRecord.firstName || ''} ${clientRecord.lastName || ''}`.trim();

    const clientForChat = {
      client_id: clientId,
      name: clientName,
      preferred_contact_type: clientRecord.preferredContactType || clientRecord.preferred_contact_type,
      cell: clientRecord.cell,
      email: clientRecord.email,
      store_id: clientRecord.storeId || clientRecord.store_id,
    };

    // Clear any existing reminder/config context
    setMsgReminderId(null);
    setMsgReminderDescription('');
    setMsgClient({
      tag_action_config_id: null,
      life_event_action_config_id: null,
      sales_opp_action_config_id: null,
      custom_event_action_config_id: null,
    });

    // Get client payment status and chat history
    getClientPaymentStatus({ client: clientForChat, auth });
    await getClientChat(clientForChat);

    setShowChatDrawer(true);
  };

  const closeChatDrawer = () => {
    setShowChatDrawer(false);
    resetAutoReminderId();
  };

  // Appointment modal handlers
  const handleScheduleAppointment = async record => {
    setAppointmentClient(record);

    // If client has an existing appointment, fetch full details for editing
    if (record.appointments?.[0]?.id) {
      try {
        const response = await getAppointment(record.appointments[0].id);
        // API may return data directly or wrapped in { data: ... }
        const fullAppointment = response?.data || response;
        setEditAppointment(fullAppointment);
        setShowAppointmentModal(true);
      } catch (error) {
        console.error('Failed to fetch appointment details:', error);
        setEditAppointment(null);
        setShowAppointmentModal(true);
      }
    } else {
      setEditAppointment(null);
      setShowAppointmentModal(true);
    }
  };

  const handleAppointmentSuccess = () => {
    // Refetch audience to update appointment data
    refetchAudience();
    setShowAppointmentModal(false);
    setAppointmentClient(null);
    setEditAppointment(null);
  };

  const handleCloseAppointmentModal = () => {
    setShowAppointmentModal(false);
    setAppointmentClient(null);
    setEditAppointment(null);
  };

  // Get label for exclusion filter
  const getExclusionLabel = () => {
    if (filters.excluded === true) return 'Excluded';
    if (filters.excluded === false) return 'Included';
    return 'Status';
  };

  // Get label for appointment filter
  const getAppointmentLabel = () => {
    if (filters.hasAppointment === true) return 'Has Appt';
    if (filters.hasAppointment === false) return 'No Appt';
    return 'Appointment';
  };

  const exclusionMenuItems = [
    { key: 'all', label: 'All', onClick: () => handleFilterChange('excluded', null) },
    { key: 'included', label: 'Included Only', onClick: () => handleFilterChange('excluded', false) },
    { key: 'excluded', label: 'Excluded Only', onClick: () => handleFilterChange('excluded', true) },
  ];

  const appointmentMenuItems = [
    { key: 'all', label: 'All', onClick: () => handleFilterChange('hasAppointment', null) },
    { key: 'hasAppointment', label: 'Has Appointment', onClick: () => handleFilterChange('hasAppointment', true) },
    { key: 'noAppointment', label: 'No Appointment', onClick: () => handleFilterChange('hasAppointment', false) },
  ];

  const breadcrumb = [{ path: '/Events', title: 'Events' }, { title: event.name || 'Event Details' }];

  if (error) {
    return (
      <StandardLayoutV2
        title="Event Details"
        breadcrumb={breadcrumb}
        classProps="event-details-page"
        containerStyles={{ backgroundColor: '#fff', padding: '24px', borderRadius: '8px' }}
      >
        <Empty description="Failed to load event details. Please try again." />
      </StandardLayoutV2>
    );
  }

  if (isLoadingEvent) {
    return (
      <StandardLayoutV2
        title="Event Details"
        breadcrumb={breadcrumb}
        classProps="event-details-page"
        containerStyles={{ backgroundColor: '#fff', padding: '24px', borderRadius: '8px' }}
      >
        <div className="loading-container">
          <Spin size="large" />
        </div>
      </StandardLayoutV2>
    );
  }

  return (
    <StandardLayoutV2
      title={event.name || 'Event Details'}
      breadcrumb={breadcrumb}
      classProps="event-details-page"
      containerStyles={{ backgroundColor: '#fff', padding: '24px', borderRadius: '8px' }}
    >
      {/* Header with date range and actions */}
      <div className="event-details-header flex-row-spacebetween-center m-btm-24">
        <p className="event-date-range">{formatDateRange()}</p>

        <div className="event-actions flex-row-nospacebetween-nowrap gap-8">
          <Button
            type="default"
            icon={<FontAwesomeIcon icon={['fal', 'link']} />}
            onClick={() => setShowLinksModal(true)}
          >
            Event Links
          </Button>
          <Button type="primary" icon={<FontAwesomeIcon icon={['fal', 'edit']} />} onClick={handleEditDetails}>
            Edit Details
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <EventKPICards
        event={event}
        totalAudience={audienceCount}
        recentContactPercentage={recentContactPercentage}
        recentContactCount={recentContactCount}
        salesAmount={salesAttributionData?.salesAmount}
        isLoadingSales={isLoadingSales}
      />

      {/* Audience Table Section */}
      <div className="audience-section m-top-24">
        <div className="audience-header flex-row-spacebetween-center m-btm-16">
          <div className="flex-row-nospacebetween-nowrap gap-8">
            <Dropdown menu={{ items: exclusionMenuItems }} trigger={['click']}>
              <Button
                icon={<FontAwesomeIcon icon={['fal', 'user-check']} />}
                className={filters.excluded !== false ? 'filter-active' : ''}
              >
                {getExclusionLabel()}
                <FontAwesomeIcon icon={['fal', 'chevron-down']} className="m-left-8" />
              </Button>
            </Dropdown>
            <Dropdown menu={{ items: appointmentMenuItems }} trigger={['click']}>
              <Button
                icon={<FontAwesomeIcon icon={['fal', 'calendar-check']} />}
                className={filters.hasAppointment !== null ? 'filter-active' : ''}
              >
                {getAppointmentLabel()}
                <FontAwesomeIcon icon={['fal', 'chevron-down']} className="m-left-8" />
              </Button>
            </Dropdown>
            {hasActiveFilters && (
              <Button type="link" onClick={clearAllFilters} className="clear-filters-btn">
                Reset filters
              </Button>
            )}
          </div>
          <Input.Search placeholder="Search" allowClear onSearch={handleSearch} style={{ width: 200 }} />
        </div>

        <AudienceTable
          audience={displayedAudience}
          loading={isLoadingAudience}
          eventId={eventId}
          event={event}
          pagination={{
            current: currentPage,
            pageSize,
            total: filteredTotal,
            onChange: page => setCurrentPage(page),
          }}
          sortConfig={sortConfig}
          onSort={handleSort}
          onMessage={handleOpenChat}
          onScheduleAppointment={handleScheduleAppointment}
        />
      </div>

      {/* Event Links Modal */}
      <EventLinksModal open={showLinksModal} onClose={() => setShowLinksModal(false)} event={event} />

      {/* Chat Drawer */}
      <Drawer
        styles={{
          body: { padding: 0, overflow: 'hidden' },
        }}
        closable={false}
        push={false}
        destroyOnClose
        width={750}
        open={showChatDrawer}
        onClose={closeChatDrawer}
      >
        <div style={{ height: '100vh' }}>
          <ConversationWindow setDrawerToggle={closeChatDrawer} chatIconColor="#EBEBEB" type="right" />
        </div>
      </Drawer>

      {/* Appointment Modal */}
      <AddEditAppointment
        isOpen={showAppointmentModal}
        onClose={handleCloseAppointmentModal}
        onSuccess={handleAppointmentSuccess}
        allAssoc={associates}
        clientIdProp={appointmentClient?.clientId || appointmentClient?.id}
        clientName={
          appointmentClient?.name || `${appointmentClient?.firstName || ''} ${appointmentClient?.lastName || ''}`.trim()
        }
        eventId={parseInt(eventId, 10)}
        eventName={event?.name}
        disableEventSelect
        associateIdProp={appointmentClient?.associate?.id}
        eventStartDate={event?.startDate}
        eventEndDate={event?.endDate}
        appointment={editAppointment}
      />
    </StandardLayoutV2>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(EventDetails);
